import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Camera, ScanLine, Pencil, Tag } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import {
  Drawer,
  useToast,
  SkeletonLoader,
} from "@/components/system";
import { useFoodSearch, type FoodSearchHit } from "@/hooks/use-food-search";
import { lookupByBarcode, offToFoodInsert, type OffProduct } from "@/api/openfoodfacts";
import {
  Row,
  FormInput,
  FormSelect,
  PrimaryButton,
  SecondaryButton,
  DialogFooter,
} from "@/components/forms";
import { formatError } from "@/lib/format-error";

/**
 * Add a PantryItem (SPEC 1.2.1). Reuses the food-search pattern from
 * LogMealDialog so the user can pick from cache or OFF.
 */

const LOCATIONS = [
  { value: "fridge", label: "Fridge" },
  { value: "freezer", label: "Freezer" },
  { value: "shelf", label: "Shelf" },
  { value: "counter", label: "Counter" },
  { value: "spice_rack", label: "Spice rack" },
  { value: "wine_storage", label: "Wine storage" },
  { value: "other", label: "Other" },
];

const PRIORITIES = [
  { value: "", label: "—" },
  { value: "essential", label: "Essential" },
  { value: "standard", label: "Standard" },
  { value: "optional", label: "Optional" },
];

export function AddPantryItemDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();
  const toast = useToast();

  const [tab, setTab] = useState<"search" | "barcode" | "photo" | "snap_label" | "manual">(
    "search",
  );
  const [query, setQuery] = useState("");
  const [foodId, setFoodId] = useState<string | null>(null);
  // BW20.3c: which step of the 5-source chain produced the selected food.
  // Stamped onto pantry_item.lookup_source so /pantry can render a chip
  // and the BW20.3d refresh cron can prioritise low-confidence rows.
  const [lookupSource, setLookupSource] = useState<
    "off" | "usda" | "photo_label" | "ai" | "manual"
  >("manual");
  const [foodLabel, setFoodLabel] = useState("");
  const [foodBrand, setFoodBrand] = useState<string | null>(null);
  const [foodKcalPer100, setFoodKcalPer100] = useState(0);
  const [foodImageUrl, setFoodImageUrl] = useState<string | null>(null);
  const [foodMacros, setFoodMacros] = useState<
    | {
        protein_g?: number;
        carbs_g?: number;
        fat_g?: number;
        fiber_g?: number;
      }
    | null
  >(null);
  const [foodBasis, setFoodBasis] = useState("per_100g");
  const [location, setLocation] = useState("shelf");
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState("g");
  const [purchased, setPurchased] = useState(today());
  const [expires, setExpires] = useState("");
  const [staple, setStaple] = useState(false);
  const [restockPriority, setRestockPriority] = useState("");
  // Snap-label tab: holds extracted macros until user confirms the product name.
  const [snapResult, setSnapResult] = useState<SnapLabelResult | null>(null);
  const [snapName, setSnapName] = useState("");

  // Pantry hits make no sense in the add-to-pantry flow itself.
  const { hits: rawHits, isLoading } = useFoodSearch(query);
  const hits = rawHits.filter((h) => h.kind !== "pantry");

  const promote = useMutation({
    mutationFn: async (hit: Extract<FoodSearchHit, { kind: "off" }>) => {
      if (!userId) throw new Error("Not signed in");
      const { data, error } = await supabase
        .from("food")
        .insert(offToFoodInsert(hit.product, userId))
        .select()
        .single();
      if (error) throw error;
      return data;
    },
  });

  // Shared "user picked / created a food" handler. All three new entry paths
  // (barcode, photo, manual) end here so the rest of the dialog (qty / unit
  // / location form) stays the same.
  function setSelectedFood(args: {
    id: string;
    name: string;
    brand: string | null;
    basis: string;
    kcalPer100: number;
    macros: {
      protein_g?: number | null;
      carbs_g?: number | null;
      fat_g?: number | null;
      fiber_g?: number | null;
    } | null;
    imageUrl: string | null;
  }) {
    setFoodId(args.id);
    setFoodLabel(args.name);
    setFoodBrand(args.brand);
    setFoodKcalPer100(args.kcalPer100);
    setFoodImageUrl(args.imageUrl);
    setFoodMacros(
      args.macros
        ? {
            protein_g: args.macros.protein_g ?? 0,
            carbs_g: args.macros.carbs_g ?? 0,
            fat_g: args.macros.fat_g ?? 0,
            fiber_g: args.macros.fiber_g ?? 0,
          }
        : null,
    );
    setFoodBasis(args.basis);
    setUnit(args.basis === "per_100ml" ? "ml" : args.basis === "per_unit" ? "pcs" : "g");
  }

  const insertOffProduct = useMutation({
    mutationFn: async (product: OffProduct) => {
      if (!userId) throw new Error("Not signed in");
      const { data, error } = await supabase
        .from("food")
        .insert(offToFoodInsert(product, userId))
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (food, product) => {
      const n = product.nutriments ?? {};
      setLookupSource("off");
      setSelectedFood({
        id: food.id,
        name: food.name,
        brand: food.brand,
        basis: food.basis,
        kcalPer100: Number(n["energy-kcal_100g"] ?? 0),
        macros: {
          protein_g: Number(n["proteins_100g"] ?? 0),
          carbs_g: Number(n["carbohydrates_100g"] ?? 0),
          fat_g: Number(n["fat_100g"] ?? 0),
          fiber_g: Number(n["fiber_100g"] ?? 0),
        },
        imageUrl: product.image_front_url ?? product.image_thumb_url ?? null,
      });
    },
    onError: (e) => toast.show(`Couldn't import: ${formatError(e)}`),
  });

  const insertExtracted = useMutation({
    mutationFn: async (label: ExtractedLabel) => {
      if (!userId) throw new Error("Not signed in");
      const per100 = label.per_100g;
      const insert = {
        user_id: userId,
        name: label.name?.trim() || "Untitled item",
        brand: label.brand?.trim() || null,
        basis: "per_100g" as const,
        kcal: Number(per100.kcal ?? 0),
        kcal_per_100g: per100.kcal,
        protein_g_per_100g: per100.protein_g,
        fat_g_per_100g: per100.fat_g,
        carb_g_per_100g: per100.carb_g,
        fiber_g_per_100g: per100.fiber_g,
        sugar_g_per_100g: per100.sugar_g,
        saturated_fat_g_per_100g: per100.saturated_fat_g,
        na_mg_per_100g: per100.sodium_mg,
        ca_mg_per_100g: per100.ca_mg,
        fe_mg_per_100g: per100.fe_mg,
        mg_mg_per_100g: per100.mg_mg,
        k_mg_per_100g: per100.k_mg,
        zn_mg_per_100g: per100.zn_mg,
        vit_c_mg_per_100g: per100.vit_c_mg,
        vit_d_ug_per_100g: per100.vit_d_ug,
        b6_mg_per_100g: per100.vit_b6_mg,
        b12_ug_per_100g: per100.vit_b12_ug,
        folate_ug_per_100g: per100.folate_ug,
        nutrition_source: "photo_extract" as const,
        nutrition_confidence: label.confidence,
        macros: {
          c: per100.carb_g ?? 0,
          p: per100.protein_g ?? 0,
          f: per100.fat_g ?? 0,
          fiber: per100.fiber_g ?? 0,
        },
        micros: {} as Record<string, never>,
        diet_tags: [] as string[],
        store_tags: [] as string[],
        available_locations: [] as string[],
        source: "manual" as const,
        is_favorite: false,
        is_user_overridden: false,
        notes: "",
      };
      const { data, error } = await supabase
        .from("food")
        .insert(insert)
        .select()
        .single();
      if (error) throw error;
      return { food: data, label };
    },
    onSuccess: ({ food, label }) => {
      setLookupSource("photo_label");
      setSelectedFood({
        id: food.id,
        name: food.name,
        brand: food.brand,
        basis: food.basis,
        kcalPer100: Number(label.per_100g.kcal ?? 0),
        macros: {
          protein_g: label.per_100g.protein_g ?? 0,
          carbs_g: label.per_100g.carb_g ?? 0,
          fat_g: label.per_100g.fat_g ?? 0,
          fiber_g: label.per_100g.fiber_g ?? 0,
        },
        imageUrl: null,
      });
    },
    onError: (e) => toast.show(`Couldn't save: ${formatError(e)}`),
  });

  // BW20.3c: chain result from /api/pantry/lookup. The chain may have run
  // OFF (with or without translate retry), USDA, or AI estimate. We persist
  // the food row with the right nutrition_source then mark lookup_source on
  // the eventual pantry_item insert.
  const insertChainResult = useMutation({
    mutationFn: async (args: {
      result: ChainResultPayload;
      source: "off" | "usda" | "ai";
    }) => {
      if (!userId) throw new Error("Not signed in");
      const p = args.result.per_100g;
      const insert = {
        user_id: userId,
        source_id: args.result.code ?? null,
        name: args.result.name?.trim() || "Untitled item",
        brand: args.result.brand?.trim() || null,
        basis: "per_100g" as const,
        kcal: Number(p.kcal ?? 0),
        kcal_per_100g: p.kcal,
        protein_g_per_100g: p.protein_g,
        carb_g_per_100g: p.carbs_g,
        fat_g_per_100g: p.fat_g,
        fiber_g_per_100g: p.fiber_g,
        sugar_g_per_100g: p.sugar_g,
        saturated_fat_g_per_100g: p.saturated_fat_g,
        na_mg_per_100g: p.sodium_mg,
        image_url: args.result.image_url ?? null,
        nutrition_source:
          args.source === "off"
            ? ("off" as const)
            : args.source === "usda"
              ? ("usda" as const)
              : ("ai_estimate" as const),
        nutrition_confidence: args.source === "ai" ? ("low" as const) : ("medium" as const),
        macros: {
          c: p.carbs_g ?? 0,
          p: p.protein_g ?? 0,
          f: p.fat_g ?? 0,
          fiber: p.fiber_g ?? 0,
        },
        micros: {} as Record<string, never>,
        diet_tags: [] as string[],
        store_tags: [] as string[],
        available_locations: [] as string[],
        source: args.source === "ai" ? ("manual" as const) : args.source,
        is_favorite: false,
        is_user_overridden: false,
        notes: "",
      };
      const { data, error } = await supabase
        .from("food")
        .insert(insert)
        .select()
        .single();
      if (error) throw error;
      return { food: data, args };
    },
    onSuccess: ({ food, args }) => {
      const p = args.result.per_100g;
      setLookupSource(args.source === "ai" ? "ai" : args.source);
      setSelectedFood({
        id: food.id,
        name: food.name,
        brand: food.brand,
        basis: food.basis,
        kcalPer100: Number(p.kcal ?? 0),
        macros: {
          protein_g: p.protein_g ?? 0,
          carbs_g: p.carbs_g ?? 0,
          fat_g: p.fat_g ?? 0,
          fiber_g: p.fiber_g ?? 0,
        },
        imageUrl: args.result.image_url ?? null,
      });
    },
    onError: (e) => toast.show(`Couldn't save: ${formatError(e)}`),
  });

  // Snap-label tab: create food row from the lightweight label extractor result.
  const insertSnapLabel = useMutation({
    mutationFn: async (args: { name: string; result: SnapLabelResult }) => {
      if (!userId) throw new Error("Not signed in");
      const r = args.result;
      const kcal = r.kcal ?? 0;
      const { data, error } = await supabase
        .from("food")
        .insert({
          user_id: userId,
          name: args.name.trim() || "Scanned label item",
          brand: null,
          basis: "per_100g" as const,
          kcal,
          kcal_per_100g: r.kcal,
          protein_g_per_100g: r.protein_g,
          carb_g_per_100g: r.carbs_g,
          fat_g_per_100g: r.fat_g,
          fiber_g_per_100g: r.fiber_g,
          sugar_g_per_100g: r.sugar_g,
          saturated_fat_g_per_100g: r.saturated_fat_g,
          na_mg_per_100g: r.sodium_mg,
          nutrition_source: "photo_extract" as const,
          nutrition_confidence: "medium" as const,
          macros: {
            c: r.carbs_g ?? 0,
            p: r.protein_g ?? 0,
            f: r.fat_g ?? 0,
            fiber: r.fiber_g ?? 0,
          },
          micros: {} as Record<string, never>,
          diet_tags: [] as string[],
          store_tags: [] as string[],
          available_locations: [] as string[],
          source: "manual" as const,
          is_favorite: false,
          is_user_overridden: false,
          notes: "",
        })
        .select()
        .single();
      if (error) throw error;
      return { food: data, args };
    },
    onSuccess: ({ food, args }) => {
      const r = args.result;
      setLookupSource("photo_label");
      setSelectedFood({
        id: food.id,
        name: food.name,
        brand: null,
        basis: food.basis,
        kcalPer100: r.kcal ?? 0,
        macros: {
          protein_g: r.protein_g ?? 0,
          carbs_g: r.carbs_g ?? 0,
          fat_g: r.fat_g ?? 0,
          fiber_g: r.fiber_g ?? 0,
        },
        imageUrl: null,
      });
      setSnapResult(null);
      setSnapName("");
    },
    onError: (e) => toast.show(`Couldn't save: ${formatError(e)}`),
  });

  const insertManual = useMutation({
    mutationFn: async (entry: ManualEntry) => {
      if (!userId) throw new Error("Not signed in");
      // Confidence: low when only macros are filled; medium when 5+ micros
      // are filled (unlikely via this form). Per spec.
      const filledMicros = [
        entry.b12_ug,
        entry.fe_mg,
        entry.vit_d_ug,
        entry.ca_mg,
        entry.zn_mg,
      ].filter((v) => typeof v === "number").length;
      const confidence = filledMicros >= 5 ? "medium" : "low";
      const sodiumMg =
        entry.salt_g != null ? Math.round(entry.salt_g * 393) : null;
      const insert = {
        user_id: userId,
        name: entry.name.trim(),
        brand: entry.brand?.trim() || null,
        basis: "per_100g" as const,
        kcal: Number(entry.kcal ?? 0),
        kcal_per_100g: entry.kcal,
        protein_g_per_100g: entry.protein_g,
        fat_g_per_100g: entry.fat_g,
        saturated_fat_g_per_100g: entry.saturated_fat_g,
        carb_g_per_100g: entry.carb_g,
        sugar_g_per_100g: entry.sugar_g,
        fiber_g_per_100g: entry.fiber_g,
        na_mg_per_100g: sodiumMg,
        nutrition_source: "manual" as const,
        nutrition_confidence: confidence,
        macros: {
          c: entry.carb_g ?? 0,
          p: entry.protein_g ?? 0,
          f: entry.fat_g ?? 0,
          fiber: entry.fiber_g ?? 0,
        },
        micros: {} as Record<string, never>,
        diet_tags: [] as string[],
        store_tags: [] as string[],
        available_locations: [] as string[],
        source: "manual" as const,
        is_favorite: false,
        is_user_overridden: false,
        notes: "",
      };
      const { data, error } = await supabase
        .from("food")
        .insert(insert)
        .select()
        .single();
      if (error) throw error;
      return { food: data, entry };
    },
    onSuccess: ({ food, entry }) => {
      setLookupSource("manual");
      setSelectedFood({
        id: food.id,
        name: food.name,
        brand: food.brand,
        basis: food.basis,
        kcalPer100: entry.kcal ?? 0,
        macros: {
          protein_g: entry.protein_g,
          carbs_g: entry.carb_g,
          fat_g: entry.fat_g,
          fiber_g: entry.fiber_g,
        },
        imageUrl: null,
      });
    },
    onError: (e) => toast.show(`Couldn't save: ${formatError(e)}`),
  });

  const add = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("Not signed in");
      if (!foodId) throw new Error("Pick a food");
      if (!qty) throw new Error("Quantity required");
      const { data, error } = await supabase
        .from("pantry_item")
        .insert({
          user_id: userId,
          food_id: foodId,
          location,
          qty: Number(qty),
          unit,
          opened: false,
          purchased,
          expires: expires || null,
          staple,
          restock_priority: restockPriority || null,
          lookup_source: lookupSource,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["pantry", userId] });
      toast.show(`${foodLabel} added to pantry.`);
      reset();
      onClose();
    },
    onError: (e) => toast.show(`Couldn't add: ${formatError(e)}`),
  });

  function reset() {
    setQuery("");
    setFoodId(null);
    setFoodLabel("");
    setFoodBrand(null);
    setFoodKcalPer100(0);
    setFoodImageUrl(null);
    setFoodMacros(null);
    setFoodBasis("per_100g");
    setLocation("shelf");
    setQty("");
    setUnit("g");
    setPurchased(today());
    setExpires("");
    setStaple(false);
    setRestockPriority("");
    setLookupSource("manual");
    setSnapResult(null);
    setSnapName("");
  }

  async function pick(hit: FoodSearchHit) {
    try {
      let id: string;
      let basis: string;
      let label: string;
      let brand: string | null = null;
      let kcalPer100 = 0;
      let imageUrl: string | null = null;
      let nutriments:
        | {
            protein_g?: number;
            carbs_g?: number;
            fat_g?: number;
            fiber_g?: number;
          }
        | null = null;
      if (hit.kind === "cache") {
        id = hit.id;
        basis = hit.basis;
        label = hit.name;
        brand = hit.brand;
        kcalPer100 = hit.kcalPer100;
        // Cache hit: the food row was previously ingested via some chain
        // step. We don't reach back into food.nutrition_source from here
        // so stamp 'manual' as a safe default; a future pass can read
        // food.nutrition_source and copy it forward.
        setLookupSource("manual");
      } else if (hit.kind === "pantry") {
        // Filtered out at the consumer; keep narrowing exhaustive.
        return;
      } else {
        const promoted = await promote.mutateAsync(hit);
        id = promoted.id;
        basis = promoted.basis;
        label = promoted.name;
        brand = hit.brand;
        kcalPer100 = hit.kcalPer100;
        imageUrl = hit.product.image_front_url ?? hit.product.image_thumb_url ?? null;
        const n = hit.product.nutriments ?? {};
        nutriments = {
          protein_g: Number(n["proteins_100g"] ?? 0),
          carbs_g: Number(n["carbohydrates_100g"] ?? 0),
          fat_g: Number(n["fat_100g"] ?? 0),
          fiber_g: Number(n["fiber_100g"] ?? 0),
        };
        setLookupSource("off");
      }
      setFoodId(id);
      setFoodLabel(label);
      setFoodBrand(brand);
      setFoodKcalPer100(kcalPer100);
      setFoodImageUrl(imageUrl);
      setFoodMacros(nutriments);
      setFoodBasis(basis);
      setUnit(basis === "per_100ml" ? "ml" : basis === "per_unit" ? "pcs" : "g");
    } catch (e) {
      toast.show(`Couldn't pick item: ${formatError(e)}`);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Add pantry item"
    >
      <PantryTabBar tab={tab} onChange={setTab} />
      {tab === "barcode" && !foodId && (
        <BarcodePanel
          onProduct={(p) => insertOffProduct.mutate(p)}
          isSaving={insertOffProduct.isPending}
        />
      )}
      {tab === "photo" && !foodId && (
        <PhotoPanel
          onExtracted={(label) => insertExtracted.mutate(label)}
          isSaving={insertExtracted.isPending}
        />
      )}
      {tab === "snap_label" && !foodId && (
        <SnapLabelPanel
          snapResult={snapResult}
          snapName={snapName}
          onSnapped={setSnapResult}
          onClear={() => { setSnapResult(null); setSnapName(""); }}
          onNameChange={setSnapName}
          onSave={() => {
            if (snapResult) insertSnapLabel.mutate({ name: snapName, result: snapResult });
          }}
          isSaving={insertSnapLabel.isPending}
        />
      )}
      {tab === "manual" && !foodId && (
        <ManualPanel
          onSubmit={(entry) => insertManual.mutate(entry)}
          isSaving={insertManual.isPending}
        />
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate();
        }}
        style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
      >
        {!foodId && tab === "search" && (
          <>
            <div style={{ position: "relative" }}>
              <Search
                size={16}
                strokeWidth={1.5}
                style={{
                  position: "absolute",
                  left: "var(--space-3)",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--fg-3)",
                  pointerEvents: "none",
                }}
              />
              <input
                type="search"
                placeholder="Search food"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{
                  width: "100%",
                  height: 38,
                  padding: "0 var(--space-3) 0 calc(var(--space-3) + 22px)",
                  background: "var(--bg-3)",
                  color: "var(--fg)",
                  border: "1px solid var(--hair-2)",
                  borderRadius: "var(--radius-card)",
                  fontFamily: "var(--font-sans)",
                  fontSize: "var(--type-body)",
                }}
              />
            </div>

            {query.trim().length >= 2 && (
              <ResultsCondensed
                hits={hits}
                isLoading={isLoading}
                isPromoting={promote.isPending}
                onPick={pick}
                query={query}
                onChainResult={(r, source) => insertChainResult.mutate({ result: r, source })}
                onPhotoCta={() => setTab("snap_label")}
              />
            )}
          </>
        )}

        {foodId && (
          <>
            <div
              style={{
                padding: "var(--space-3)",
                background: "var(--accent-wash)",
                border: "1px solid var(--accent-dim)",
                borderRadius: "var(--radius-card)",
                display: "flex",
                gap: "var(--space-3)",
                alignItems: "center",
              }}
            >
              {foodImageUrl ? (
                <img
                  src={foodImageUrl}
                  alt=""
                  width={56}
                  height={56}
                  style={{
                    borderRadius: "var(--radius-card)",
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
              ) : null}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    color: "var(--fg)",
                    fontWeight: 500,
                    fontSize: "var(--type-body)",
                  }}
                >
                  ✓ {foodLabel}
                </div>
                {foodBrand ? (
                  <div
                    style={{
                      color: "var(--fg-3)",
                      fontSize: "var(--type-meta)",
                      marginTop: 2,
                    }}
                  >
                    {foodBrand}
                  </div>
                ) : null}
                {(foodKcalPer100 > 0 || foodMacros) && (
                  <div
                    className="mono"
                    style={{
                      color: "var(--fg-2)",
                      fontSize: "var(--type-body-s)",
                      marginTop: 6,
                      lineHeight: 1.4,
                    }}
                  >
                    Per 100g: {Math.round(foodKcalPer100)} kcal
                    {foodMacros && (
                      <>
                        {" · P "}
                        {Math.round(Number(foodMacros.protein_g ?? 0))}g · C{" "}
                        {Math.round(Number(foodMacros.carbs_g ?? 0))}g · F{" "}
                        {Math.round(Number(foodMacros.fat_g ?? 0))}g
                        {foodMacros.fiber_g
                          ? ` · Fiber ${Math.round(Number(foodMacros.fiber_g))}g`
                          : ""}
                      </>
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setFoodId(null);
                  setFoodLabel("");
                  setFoodBrand(null);
                  setFoodImageUrl(null);
                  setFoodMacros(null);
                  setFoodKcalPer100(0);
                }}
                style={{
                  background: "transparent",
                  color: "var(--fg-3)",
                  border: "1px solid var(--hair-2)",
                  borderRadius: "var(--radius-chip)",
                  padding: "4px 10px",
                  fontSize: "var(--type-meta)",
                  cursor: "pointer",
                  alignSelf: "flex-start",
                }}
              >
                Clear
              </button>
            </div>

            <Row>
              <FormInput
                label="Quantity"
                inputMode="decimal"
                value={qty}
                onChange={setQty}
                placeholder={foodBasis === "per_unit" ? "1" : "500"}
                required
              />
              <FormSelect
                label="Unit"
                value={unit}
                onChange={setUnit}
                options={[
                  { value: "g", label: "g" },
                  { value: "ml", label: "ml" },
                  { value: "pcs", label: "pcs" },
                ]}
              />
              <FormSelect
                label="Location"
                value={location}
                onChange={setLocation}
                options={LOCATIONS}
              />
            </Row>

            <Row>
              <FormInput
                label="Purchased"
                type="date"
                value={purchased}
                onChange={setPurchased}
              />
              <FormInput
                label="Expires (optional)"
                type="date"
                value={expires}
                onChange={setExpires}
              />
            </Row>

            <Row>
              <FormSelect
                label="Restock priority"
                value={restockPriority}
                onChange={setRestockPriority}
                options={PRIORITIES}
              />
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--space-3)",
                  fontSize: "var(--type-body-s)",
                  color: "var(--fg-2)",
                  alignSelf: "flex-end",
                  paddingBottom: "var(--space-2)",
                }}
              >
                <input
                  type="checkbox"
                  checked={staple}
                  onChange={(e) => setStaple(e.target.checked)}
                  style={{ width: 16, height: 16 }}
                />
                Always keep in stock (staple)
              </label>
            </Row>
          </>
        )}

        <DialogFooter>
          <SecondaryButton
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={add.isPending}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton type="submit" disabled={add.isPending || !foodId || !qty}>
            {add.isPending ? "Saving…" : "Add"}
          </PrimaryButton>
        </DialogFooter>
      </form>
    </Drawer>
  );
}

function ResultsCondensed({
  hits,
  isLoading,
  isPromoting,
  onPick,
  query,
  onChainResult,
  onPhotoCta,
}: {
  hits: FoodSearchHit[];
  isLoading: boolean;
  isPromoting: boolean;
  onPick: (h: FoodSearchHit) => void;
  query: string;
  onChainResult: (
    result: {
      name: string;
      brand: string | null;
      per_100g: {
        kcal: number | null;
        protein_g: number | null;
        carbs_g: number | null;
        fat_g: number | null;
        fiber_g: number | null;
        sugar_g: number | null;
        saturated_fat_g: number | null;
        sodium_mg: number | null;
      };
      image_url: string | null;
      code: string | null;
    },
    source: "off" | "usda" | "ai",
  ) => void;
  onPhotoCta: () => void;
}) {
  if (isLoading) return <SkeletonLoader variant="block" count={3} />;
  if (hits.length === 0)
    return (
      <LookupChainPanel
        query={query}
        onResult={onChainResult}
        onPhotoCta={onPhotoCta}
      />
    );
  return (
    <ul
      className="haus-pantry-results"
      style={{
        listStyle: "none",
        margin: 0,
        padding: 0,
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-2)",
        maxHeight: 360,
        overflow: "auto",
      }}
    >
      <style>{`
        .haus-pantry-results .haus-pantry-result-btn {
          width: 100%;
          padding: var(--space-3);
          background: var(--bg-2);
          border: 1px solid var(--hair-2);
          border-radius: var(--radius-card);
          text-align: left;
          cursor: pointer;
          font-size: var(--type-body-s);
          color: var(--fg);
          display: grid;
          grid-template-columns: 48px 1fr max-content;
          gap: var(--space-3);
          align-items: center;
          transition: background var(--dur-fast) var(--ease-out),
            border-color var(--dur-fast) var(--ease-out);
        }
        .haus-pantry-results .haus-pantry-result-btn:hover {
          background: var(--bg-3);
          border-color: var(--accent-dim);
        }
        .haus-pantry-results .haus-pantry-result-btn:focus-visible {
          outline: 2px solid var(--accent);
          outline-offset: 2px;
        }
        .haus-pantry-results .haus-pantry-result-btn:disabled {
          opacity: 0.6;
          cursor: progress;
        }
      `}</style>
      {hits.map((h) => {
        const imageUrl =
          h.kind === "off"
            ? h.product.image_front_url ?? h.product.image_thumb_url ?? null
            : null;
        const key =
          h.kind === "cache"
            ? `c-${h.id}`
            : h.kind === "off"
              ? `o-${h.offCode}`
              : `p-${h.kind}`;
        return (
          <li key={key}>
            <button
              type="button"
              onClick={() => onPick(h)}
              disabled={isPromoting}
              className="haus-pantry-result-btn"
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt=""
                  width={48}
                  height={48}
                  loading="lazy"
                  style={{
                    borderRadius: "var(--radius-card)",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <div
                  aria-hidden
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: "var(--radius-card)",
                    background: "var(--chip)",
                  }}
                />
              )}
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    color: "var(--fg)",
                    fontSize: "var(--type-body-s)",
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {h.name}
                </div>
                <div
                  style={{
                    color: "var(--fg-3)",
                    fontSize: "var(--type-meta)",
                    marginTop: 2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {h.brand ?? "—"}
                  <span
                    className="mono"
                    style={{
                      marginLeft: 6,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                    }}
                  >
                    · {h.kind === "cache" ? "cache" : "OFF"}
                  </span>
                </div>
              </div>
              <div
                className="mono"
                style={{
                  color: "var(--fg-2)",
                  fontSize: "var(--type-body-s)",
                  whiteSpace: "nowrap",
                }}
              >
                {Math.round(h.kcalPer100)} kcal/100g
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function PantryTabBar({
  tab,
  onChange,
}: {
  tab: "search" | "barcode" | "photo" | "snap_label" | "manual";
  onChange: (t: "search" | "barcode" | "photo" | "snap_label" | "manual") => void;
}) {
  const tabs: Array<{
    key: "search" | "barcode" | "photo" | "snap_label" | "manual";
    label: string;
    Icon: typeof Search;
  }> = [
    { key: "search", label: "Search", Icon: Search },
    { key: "barcode", label: "Barcode", Icon: ScanLine },
    { key: "photo", label: "Photo", Icon: Camera },
    { key: "snap_label", label: "Snap label", Icon: Tag },
    { key: "manual", label: "Manual", Icon: Pencil },
  ];
  return (
    <div
      role="tablist"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${tabs.length}, 1fr)`,
        gap: 4,
        background: "var(--bg-3)",
        borderRadius: "var(--radius-card)",
        padding: 4,
        marginBottom: "var(--space-3)",
      }}
    >
      {tabs.map((t) => {
        const active = tab === t.key;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.key)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              padding: "8px 12px",
              background: active ? "var(--bg)" : "transparent",
              border: "none",
              borderRadius: "var(--radius-chip)",
              color: active ? "var(--fg)" : "var(--fg-3)",
              fontSize: "var(--type-body-s)",
              cursor: "pointer",
              fontWeight: active ? 500 : 400,
            }}
          >
            <t.Icon size={14} strokeWidth={1.6} />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

type ExtractedLabel = {
  name: string | null;
  brand: string | null;
  serving_size_g: number | null;
  per_100g: {
    kcal: number | null;
    protein_g: number | null;
    fat_g: number | null;
    saturated_fat_g: number | null;
    carb_g: number | null;
    sugar_g: number | null;
    fiber_g: number | null;
    sodium_mg: number | null;
    ca_mg: number | null;
    fe_mg: number | null;
    mg_mg: number | null;
    k_mg: number | null;
    zn_mg: number | null;
    vit_c_mg: number | null;
    vit_d_ug: number | null;
    vit_b6_mg: number | null;
    vit_b12_ug: number | null;
    folate_ug: number | null;
  };
  confidence: "high" | "medium" | "low";
  notes: string;
};

type ManualEntry = {
  name: string;
  brand?: string | null;
  kcal: number | null;
  protein_g: number | null;
  fat_g: number | null;
  saturated_fat_g: number | null;
  carb_g: number | null;
  sugar_g: number | null;
  fiber_g: number | null;
  // Salt in grams as printed on a typical EU label. Converted to sodium_mg
  // at 1g salt = 393 mg sodium.
  salt_g: number | null;
  // Tier 1 micros — optional
  b12_ug?: number | null;
  fe_mg?: number | null;
  vit_d_ug?: number | null;
  ca_mg?: number | null;
  zn_mg?: number | null;
};

function BarcodePanel({
  onProduct,
  isSaving,
}: {
  onProduct: (p: OffProduct) => void;
  isSaving: boolean;
}) {
  const [ean, setEan] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);

  async function lookup() {
    const digits = ean.trim().replace(/[^\d]/g, "");
    let normalized = digits;
    if (digits.length === 12) normalized = "0" + digits; // UPC-A -> EAN-13
    if (!/^\d{13}$/.test(normalized)) {
      setError("Barcode must be 12 or 13 digits.");
      return;
    }
    setError(null);
    setSearching(true);
    try {
      const product = await lookupByBarcode(normalized);
      if (!product) {
        setError("Not in Open Food Facts. Try Photo or Manual.");
        return;
      }
      onProduct(product);
    } catch (e) {
      setError(`Lookup failed: ${formatError(e)}`);
    } finally {
      setSearching(false);
    }
  }

  return (
    <section
      aria-label="Barcode entry"
      style={{
        padding: "var(--space-4)",
        border: "1px solid var(--hair-2)",
        borderRadius: "var(--radius-card)",
        marginBottom: "var(--space-4)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-3)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
        <ScanLine size={16} strokeWidth={1.5} style={{ color: "var(--accent)" }} />
        <span style={{ fontWeight: 500, fontSize: "var(--type-body-s)" }}>
          Look up by barcode
        </span>
      </div>
      <p style={{ margin: 0, fontSize: "var(--type-meta)", color: "var(--fg-3)", lineHeight: 1.5 }}>
        Type or paste the EAN/UPC from the back of the package. Pulls macros and
        micros from Open Food Facts.
      </p>
      <div style={{ display: "flex", gap: "var(--space-2)" }}>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={ean}
          onChange={(e) => setEan(e.target.value.replace(/[^\d]/g, ""))}
          placeholder="e.g. 7610200416261"
          style={{
            flex: 1,
            minWidth: 0,
            height: 38,
            padding: "0 var(--space-3)",
            background: "var(--bg-3)",
            color: "var(--fg)",
            border: "1px solid var(--hair-2)",
            borderRadius: "var(--radius-card)",
            fontFamily: "var(--font-mono)",
            fontSize: "var(--type-body)",
          }}
        />
        <button
          type="button"
          onClick={lookup}
          disabled={searching || isSaving || !ean}
          style={{
            height: 38,
            padding: "0 var(--space-4)",
            background: "var(--accent)",
            color: "#0a0b0d",
            border: "1px solid transparent",
            borderRadius: "var(--radius-card)",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: "var(--type-body-s)",
          }}
        >
          {searching ? "Looking up…" : isSaving ? "Saving…" : "Look up"}
        </button>
      </div>
      {error && (
        <p
          role="alert"
          style={{ margin: 0, color: "var(--brick)", fontSize: "var(--type-body-s)" }}
        >
          {error}
        </p>
      )}
    </section>
  );
}

const WORKER_URL =
  (import.meta.env.VITE_HAUS_WORKER_URL as string | undefined) ??
  "https://haus-worker.rackljo.workers.dev";

function PhotoPanel({
  onExtracted,
  isSaving,
}: {
  onExtracted: (label: ExtractedLabel) => void;
  isSaving: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setError(null);
    setBusy(true);
    try {
      const { data: sessionRes } = await supabase.auth.getSession();
      const accessToken = sessionRes.session?.access_token;
      if (!accessToken) {
        setError("Sign in to extract labels.");
        return;
      }
      const fd = new FormData();
      fd.append("image", file);
      const resp = await fetch(`${WORKER_URL}/api/foods/extract-label`, {
        method: "POST",
        headers: { authorization: `Bearer ${accessToken}` },
        body: fd,
      });
      const json = (await resp.json()) as ExtractedLabel | { error: string };
      if (!resp.ok || "error" in json) {
        setError(`Extract failed: ${"error" in json ? json.error : resp.status}`);
        return;
      }
      onExtracted(json);
    } catch (e) {
      setError(`Extract failed: ${formatError(e)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      aria-label="Label photo"
      style={{
        padding: "var(--space-4)",
        border: "1px solid var(--hair-2)",
        borderRadius: "var(--radius-card)",
        marginBottom: "var(--space-4)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-3)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
        <Camera size={16} strokeWidth={1.5} style={{ color: "var(--accent)" }} />
        <span style={{ fontWeight: 500, fontSize: "var(--type-body-s)" }}>
          Photo of nutrition label
        </span>
      </div>
      <p style={{ margin: 0, fontSize: "var(--type-meta)", color: "var(--fg-3)", lineHeight: 1.5 }}>
        Snap the back-of-pack panel. Claude reads the per-100g values; you
        review and save.
      </p>
      <label
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "var(--space-2)",
          height: 38,
          padding: "0 var(--space-4)",
          background: busy || isSaving ? "var(--bg-3)" : "var(--accent)",
          color: busy || isSaving ? "var(--fg-3)" : "#0a0b0d",
          border: "1px solid transparent",
          borderRadius: "var(--radius-card)",
          cursor: busy || isSaving ? "progress" : "pointer",
          fontWeight: 600,
          fontSize: "var(--type-body-s)",
        }}
      >
        <Camera size={14} strokeWidth={1.5} />
        {busy ? "Reading label…" : isSaving ? "Saving…" : "Take or upload photo"}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          disabled={busy || isSaving}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
            e.target.value = "";
          }}
          style={{ display: "none" }}
        />
      </label>
      {error && (
        <p
          role="alert"
          style={{ margin: 0, color: "var(--brick)", fontSize: "var(--type-body-s)" }}
        >
          {error}
        </p>
      )}
    </section>
  );
}

function ManualPanel({
  onSubmit,
  isSaving,
}: {
  onSubmit: (entry: ManualEntry) => void;
  isSaving: boolean;
}) {
  const [name, setName] = useState("");
  const [brand, setBrand] = useState("");
  const [kcal, setKcal] = useState("");
  const [protein, setProtein] = useState("");
  const [carb, setCarb] = useState("");
  const [sugar, setSugar] = useState("");
  const [fat, setFat] = useState("");
  const [satFat, setSatFat] = useState("");
  const [fiber, setFiber] = useState("");
  const [salt, setSalt] = useState("");
  const [showMicros, setShowMicros] = useState(false);
  const [b12, setB12] = useState("");
  const [fe, setFe] = useState("");
  const [vitD, setVitD] = useState("");
  const [ca, setCa] = useState("");
  const [zn, setZn] = useState("");
  const [error, setError] = useState<string | null>(null);

  function num(s: string): number | null {
    if (!s.trim()) return null;
    const n = Number(s.replace(",", "."));
    return Number.isFinite(n) ? n : null;
  }

  function submit() {
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    const kcalN = num(kcal);
    const proteinN = num(protein);
    if (kcalN == null || proteinN == null) {
      setError("kcal and protein are required so meal-plan math works.");
      return;
    }
    setError(null);
    onSubmit({
      name,
      brand: brand || null,
      kcal: kcalN,
      protein_g: proteinN,
      fat_g: num(fat),
      saturated_fat_g: num(satFat),
      carb_g: num(carb),
      sugar_g: num(sugar),
      fiber_g: num(fiber),
      salt_g: num(salt),
      b12_ug: num(b12),
      fe_mg: num(fe),
      vit_d_ug: num(vitD),
      ca_mg: num(ca),
      zn_mg: num(zn),
    });
  }

  return (
    <section
      aria-label="Manual entry"
      style={{
        padding: "var(--space-4)",
        border: "1px solid var(--hair-2)",
        borderRadius: "var(--radius-card)",
        marginBottom: "var(--space-4)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-3)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
        <Pencil size={16} strokeWidth={1.5} style={{ color: "var(--accent)" }} />
        <span style={{ fontWeight: 500, fontSize: "var(--type-body-s)" }}>
          Enter manually
        </span>
      </div>
      <Row>
        <FormInput label="Name" value={name} onChange={setName} placeholder="e.g. Tofu firm" required />
        <FormInput label="Brand" value={brand} onChange={setBrand} placeholder="optional" />
      </Row>
      <p
        style={{
          margin: 0,
          fontSize: "var(--type-meta)",
          color: "var(--fg-3)",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        Per 100g
      </p>
      <Row>
        <FormInput label="kcal" inputMode="decimal" value={kcal} onChange={setKcal} required />
        <FormInput label="Protein g" inputMode="decimal" value={protein} onChange={setProtein} required />
        <FormInput label="Fat g" inputMode="decimal" value={fat} onChange={setFat} />
      </Row>
      <Row>
        <FormInput
          label="Saturated fat g"
          inputMode="decimal"
          value={satFat}
          onChange={setSatFat}
        />
        <FormInput label="Carb g" inputMode="decimal" value={carb} onChange={setCarb} />
        <FormInput
          label="of which sugars g"
          inputMode="decimal"
          value={sugar}
          onChange={setSugar}
        />
      </Row>
      <Row>
        <FormInput label="Fiber g" inputMode="decimal" value={fiber} onChange={setFiber} />
        <FormInput
          label="Salt g"
          inputMode="decimal"
          value={salt}
          onChange={setSalt}
        />
      </Row>
      <button
        type="button"
        onClick={() => setShowMicros((v) => !v)}
        style={{
          alignSelf: "flex-start",
          background: "transparent",
          color: "var(--accent)",
          border: "1px solid var(--hair-2)",
          padding: "6px 10px",
          borderRadius: "var(--radius-chip)",
          fontSize: "var(--type-meta)",
          cursor: "pointer",
        }}
      >
        {showMicros ? "Hide micros" : "Add micros (optional)"}
      </button>
      {showMicros && (
        <>
          <Row>
            <FormInput label="B12 µg" inputMode="decimal" value={b12} onChange={setB12} />
            <FormInput label="Iron mg" inputMode="decimal" value={fe} onChange={setFe} />
            <FormInput label="Vit D µg" inputMode="decimal" value={vitD} onChange={setVitD} />
          </Row>
          <Row>
            <FormInput label="Calcium mg" inputMode="decimal" value={ca} onChange={setCa} />
            <FormInput label="Zinc mg" inputMode="decimal" value={zn} onChange={setZn} />
          </Row>
        </>
      )}
      {error && (
        <p
          role="alert"
          style={{ margin: 0, color: "var(--brick)", fontSize: "var(--type-body-s)" }}
        >
          {error}
        </p>
      )}
      <div>
        <button
          type="button"
          onClick={submit}
          disabled={isSaving}
          style={{
            height: 38,
            padding: "0 var(--space-4)",
            background: "var(--accent)",
            color: "#0a0b0d",
            border: "1px solid transparent",
            borderRadius: "var(--radius-card)",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: "var(--type-body-s)",
          }}
        >
          {isSaving ? "Saving…" : "Save and continue"}
        </button>
      </div>
    </section>
  );
}

function today(): string {
  const d = new Date();
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// ---------------------------------------------------------------------------
// BW20.3b — Snap label tab.
// Lightweight back-of-package nutrition extractor that calls
// /api/nutrition/extract-from-label-photo (returns just per-100g macros, not
// brand/name/micros). User types the product name, then hits Save. This is
// the destination for the "Snap a label" CTA from the search chain panel.
// ---------------------------------------------------------------------------

type SnapLabelResult = {
  basis: "per_100g" | "per_serving";
  serving_size_g?: number | null;
  kcal: number | null;
  protein_g: number | null;
  carbs_g: number | null;
  fiber_g: number | null;
  sugar_g: number | null;
  fat_g: number | null;
  saturated_fat_g: number | null;
  sodium_mg: number | null;
  potassium_mg: number | null;
};

function SnapLabelPanel({
  snapResult,
  snapName,
  onSnapped,
  onClear,
  onNameChange,
  onSave,
  isSaving,
}: {
  snapResult: SnapLabelResult | null;
  snapName: string;
  onSnapped: (r: SnapLabelResult) => void;
  onClear: () => void;
  onNameChange: (name: string) => void;
  onSave: () => void;
  isSaving: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setError(null);
    setBusy(true);
    try {
      const { data: sessionRes } = await supabase.auth.getSession();
      const accessToken = sessionRes.session?.access_token;
      if (!accessToken) {
        setError("Sign in to extract labels.");
        return;
      }
      const fd = new FormData();
      fd.append("image", file);
      const resp = await fetch(`${WORKER_URL}/api/nutrition/extract-from-label-photo`, {
        method: "POST",
        headers: { authorization: `Bearer ${accessToken}` },
        body: fd,
      });
      const json = (await resp.json()) as SnapLabelResult | { error: string };
      if (!resp.ok || "error" in json) {
        const msg = "error" in json ? json.error : `HTTP ${resp.status}`;
        setError(
          msg === "not_a_label"
            ? "No nutrition panel found. Point the camera at the back-of-pack table."
            : `Extract failed: ${msg}`,
        );
        return;
      }
      onSnapped(json as SnapLabelResult);
    } catch (e) {
      setError(`Extract failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      aria-label="Snap nutrition label"
      style={{
        padding: "var(--space-4)",
        border: "1px solid var(--hair-2)",
        borderRadius: "var(--radius-card)",
        marginBottom: "var(--space-4)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-3)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
        <Tag size={16} strokeWidth={1.5} style={{ color: "var(--accent)" }} />
        <span style={{ fontWeight: 500, fontSize: "var(--type-body-s)" }}>
          Snap nutrition label
        </span>
      </div>
      <p style={{ margin: 0, fontSize: "var(--type-meta)", color: "var(--fg-3)", lineHeight: 1.5 }}>
        Point at the back-of-pack nutrition table. Claude reads the per-100g numbers; enter the
        product name to save.
      </p>

      {!snapResult && (
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "var(--space-2)",
            height: 38,
            padding: "0 var(--space-4)",
            background: busy ? "var(--bg-3)" : "var(--accent)",
            color: busy ? "var(--fg-3)" : "#0a0b0d",
            border: "1px solid transparent",
            borderRadius: "var(--radius-card)",
            cursor: busy ? "progress" : "pointer",
            fontWeight: 600,
            fontSize: "var(--type-body-s)",
            alignSelf: "flex-start",
          }}
        >
          <Camera size={14} strokeWidth={1.5} />
          {busy ? "Reading label…" : "Take or upload photo"}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
              e.target.value = "";
            }}
            style={{ display: "none" }}
          />
        </label>
      )}

      {snapResult && (
        <>
          <div
            style={{
              padding: "var(--space-3)",
              background: "var(--bg-2)",
              border: "1px solid var(--hair-2)",
              borderRadius: "var(--radius-card)",
              fontSize: "var(--type-body-s)",
              lineHeight: 1.6,
            }}
          >
            <div
              className="mono"
              style={{ color: "var(--fg-2)", marginBottom: "var(--space-2)" }}
            >
              Per 100g: {Math.round(snapResult.kcal ?? 0)} kcal
              {snapResult.protein_g != null && ` · P ${Math.round(snapResult.protein_g)}g`}
              {snapResult.carbs_g != null && ` · C ${Math.round(snapResult.carbs_g)}g`}
              {snapResult.fat_g != null && ` · F ${Math.round(snapResult.fat_g)}g`}
              {snapResult.fiber_g != null && ` · Fiber ${Math.round(snapResult.fiber_g)}g`}
              {snapResult.sodium_mg != null && ` · Na ${Math.round(snapResult.sodium_mg)}mg`}
            </div>
            {snapResult.basis === "per_serving" && (
              <div style={{ color: "var(--fg-3)", fontSize: "var(--type-meta)" }}>
                Label shows per-serving values
                {snapResult.serving_size_g ? ` (${snapResult.serving_size_g}g)` : ""}.
                Figures converted to per-100g.
              </div>
            )}
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: "var(--type-meta)",
                color: "var(--fg-2)",
                marginBottom: 4,
              }}
            >
              Product name
            </label>
            <input
              type="text"
              value={snapName}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="e.g. Bio Seidentofu"
              autoFocus
              style={{
                width: "100%",
                height: 38,
                padding: "0 var(--space-3)",
                background: "var(--bg-3)",
                color: "var(--fg)",
                border: "1px solid var(--hair-2)",
                borderRadius: "var(--radius-card)",
                fontFamily: "var(--font-sans)",
                fontSize: "var(--type-body)",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div style={{ display: "flex", gap: "var(--space-2)" }}>
            <button
              type="button"
              onClick={onSave}
              disabled={isSaving || !snapName.trim()}
              style={{
                height: 36,
                padding: "0 var(--space-4)",
                background: "var(--accent)",
                color: "#0a0b0d",
                border: "1px solid transparent",
                borderRadius: "var(--radius-card)",
                cursor: isSaving || !snapName.trim() ? "not-allowed" : "pointer",
                fontWeight: 600,
                fontSize: "var(--type-body-s)",
                opacity: !snapName.trim() ? 0.5 : 1,
              }}
            >
              {isSaving ? "Saving…" : "Save and continue"}
            </button>
            <button
              type="button"
              onClick={onClear}
              style={{
                height: 36,
                padding: "0 var(--space-3)",
                background: "transparent",
                color: "var(--fg)",
                border: "1px solid var(--hair-2)",
                borderRadius: "var(--radius-card)",
                cursor: "pointer",
                fontSize: "var(--type-body-s)",
              }}
            >
              Retake
            </button>
          </div>
        </>
      )}

      {error && (
        <p
          role="alert"
          style={{ margin: 0, color: "var(--brick)", fontSize: "var(--type-body-s)" }}
        >
          {error}
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// BW20.3c — Lookup chain panel.
// Triggered from the Search empty state. Walks /api/pantry/lookup, surfaces
// step status, and either offers a "Use this match" CTA or, when no source
// found a match, the photo-of-label CTA which switches to the Photo tab.
// ---------------------------------------------------------------------------

type ChainResultPayload = {
  name: string;
  brand: string | null;
  per_100g: {
    kcal: number | null;
    protein_g: number | null;
    carbs_g: number | null;
    fat_g: number | null;
    fiber_g: number | null;
    sugar_g: number | null;
    saturated_fat_g: number | null;
    sodium_mg: number | null;
  };
  image_url: string | null;
  code: string | null;
};

type ChainResponse = {
  ok: boolean;
  source: "off" | "off_translated" | "usda" | "ai" | "none";
  product: ChainResultPayload | null;
  steps: Array<{ source: string; ok: boolean; ms: number; note?: string }>;
  duration_ms: number;
  hint?: string;
};

// Inline CSS spinner — no dep, just a 14px rotating border.
function ChainSpinner() {
  return (
    <>
      <style>{`
        @keyframes haus-spin { to { transform: rotate(360deg); } }
        .haus-chain-spinner {
          display: inline-block;
          width: 12px;
          height: 12px;
          border: 2px solid var(--hair-2);
          border-top-color: var(--accent);
          border-radius: 50%;
          animation: haus-spin 0.7s linear infinite;
          flex-shrink: 0;
        }
      `}</style>
      <span className="haus-chain-spinner" aria-hidden />
    </>
  );
}

// Progress labels shown while the chain runs. Timed to roughly match the
// real step sequence (OFF ~0.5s, translate ~1s, USDA ~2s, AI ~4s).
const CHAIN_STEPS = [
  { label: "Checking Open Food Facts…", ms: 0 },
  { label: "Translating query…", ms: 1400 },
  { label: "Checking USDA…", ms: 2800 },
  { label: "Asking Claude for an estimate…", ms: 4200 },
];

function LookupChainPanel({
  query,
  onResult,
  onPhotoCta,
}: {
  query: string;
  onResult: (
    result: ChainResultPayload,
    source: "off" | "usda" | "ai",
  ) => void;
  onPhotoCta: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<ChainResponse | null>(null);
  const [stepIdx, setStepIdx] = useState(0);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (!busy) {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
      setStepIdx(0);
      return;
    }
    // Schedule step advances. CHAIN_STEPS[0] shows immediately (ms:0).
    timersRef.current = CHAIN_STEPS.slice(1).map((step, i) =>
      setTimeout(() => setStepIdx(i + 1), step.ms),
    );
    return () => {
      timersRef.current.forEach(clearTimeout);
    };
  }, [busy]);

  async function run() {
    setBusy(true);
    setError(null);
    setResponse(null);
    try {
      const { data: sessionRes } = await supabase.auth.getSession();
      const accessToken = sessionRes.session?.access_token;
      if (!accessToken) {
        setError("Sign in to search wider.");
        return;
      }
      const resp = await fetch(`${WORKER_URL}/api/pantry/lookup`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${accessToken}`,
          "content-type": "application/json",
        },
        body: JSON.stringify({ query }),
      });
      const json = (await resp.json()) as ChainResponse | { error: string };
      if (!resp.ok || "error" in json) {
        setError(`Lookup failed: ${"error" in json ? json.error : resp.status}`);
        return;
      }
      setResponse(json);
    } catch (e) {
      setError(`Lookup failed: ${formatError(e)}`);
    } finally {
      setBusy(false);
    }
  }

  if (!response && !busy && !error) {
    return (
      <div
        style={{
          padding: "var(--space-4)",
          border: "1px solid var(--hair-2)",
          borderRadius: "var(--radius-card)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-2)",
        }}
      >
        <div style={{ fontSize: "var(--type-body-s)", color: "var(--fg-2)" }}>
          No matches yet. Try wider sources.
        </div>
        <div style={{ fontSize: "var(--type-meta)", color: "var(--fg-3)", lineHeight: 1.5 }}>
          Translates non-English names, then queries USDA, then asks Claude for a
          rough estimate. Or skip ahead and snap a photo of the back-of-pack panel.
        </div>
        <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-2)" }}>
          <button
            type="button"
            onClick={() => void run()}
            style={{
              height: 34,
              padding: "0 var(--space-3)",
              background: "var(--accent)",
              color: "#0a0b0d",
              border: "1px solid transparent",
              borderRadius: "var(--radius-card)",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "var(--type-body-s)",
            }}
          >
            Search wider
          </button>
          <button
            type="button"
            onClick={onPhotoCta}
            style={{
              height: 34,
              padding: "0 var(--space-3)",
              background: "transparent",
              color: "var(--fg)",
              border: "1px solid var(--hair-2)",
              borderRadius: "var(--radius-card)",
              cursor: "pointer",
              fontSize: "var(--type-body-s)",
            }}
          >
            Snap a label
          </button>
        </div>
      </div>
    );
  }

  if (busy) {
    return (
      <div
        style={{
          padding: "var(--space-4)",
          border: "1px solid var(--hair-2)",
          borderRadius: "var(--radius-card)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-2)",
        }}
      >
        <ul
          style={{
            margin: 0,
            padding: 0,
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {CHAIN_STEPS.map((step, i) => {
            const done = i < stepIdx;
            const active = i === stepIdx;
            return (
              <li
                key={step.label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--space-2)",
                  fontSize: "var(--type-body-s)",
                  color: done
                    ? "var(--fg-3)"
                    : active
                      ? "var(--fg)"
                      : "var(--fg-3)",
                  opacity: i > stepIdx ? 0.4 : 1,
                  transition: "opacity 0.3s, color 0.3s",
                }}
              >
                {done ? (
                  <span style={{ fontSize: 12, minWidth: 16 }}>✓</span>
                ) : active ? (
                  <ChainSpinner />
                ) : (
                  <span style={{ minWidth: 16 }} />
                )}
                {step.label}
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  if (error) {
    return (
      <div
        role="alert"
        style={{
          padding: "var(--space-4)",
          border: "1px solid var(--hair-2)",
          borderRadius: "var(--radius-card)",
          color: "var(--brick)",
          fontSize: "var(--type-body-s)",
        }}
      >
        {error}
      </div>
    );
  }

  if (!response) return null;

  const stepLabel = (s: string) =>
    s === "off" ? "Open Food Facts" :
    s === "translate" ? "Translate to English" :
    s === "off_translated" ? "OFF (translated)" :
    s === "usda" ? "USDA" :
    s === "ai" ? "AI estimate" :
    s;

  return (
    <div
      style={{
        padding: "var(--space-4)",
        border: "1px solid var(--hair-2)",
        borderRadius: "var(--radius-card)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-3)",
      }}
    >
      <ul
        style={{
          margin: 0,
          padding: 0,
          listStyle: "none",
          display: "flex",
          flexDirection: "column",
          gap: 4,
          fontSize: "var(--type-meta)",
          color: "var(--fg-3)",
        }}
      >
        {response.steps.map((s, i) => (
          <li key={`${s.source}-${i}`} style={{ display: "flex", justifyContent: "space-between" }}>
            <span>
              {s.ok ? "✓" : "·"} {stepLabel(s.source)}
              {s.note ? ` — ${s.note}` : ""}
            </span>
            <span className="mono">{s.ms}ms</span>
          </li>
        ))}
      </ul>

      {response.ok && response.product ? (
        <div
          style={{
            padding: "var(--space-3)",
            background: "var(--accent-wash)",
            border: "1px solid var(--accent-dim)",
            borderRadius: "var(--radius-card)",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <div style={{ fontWeight: 500, fontSize: "var(--type-body)" }}>
            {response.product.name}
          </div>
          {response.product.brand ? (
            <div style={{ color: "var(--fg-3)", fontSize: "var(--type-meta)" }}>
              {response.product.brand}
            </div>
          ) : null}
          <div className="mono" style={{ color: "var(--fg-2)", fontSize: "var(--type-body-s)" }}>
            Per 100g: {Math.round(Number(response.product.per_100g.kcal ?? 0))} kcal
            {" · P "}{Math.round(Number(response.product.per_100g.protein_g ?? 0))}g
            {" · C "}{Math.round(Number(response.product.per_100g.carbs_g ?? 0))}g
            {" · F "}{Math.round(Number(response.product.per_100g.fat_g ?? 0))}g
          </div>
          <div style={{ fontSize: "var(--type-meta)", color: "var(--fg-3)" }}>
            Source: {response.source}
          </div>
          <div style={{ display: "flex", gap: "var(--space-2)", marginTop: 4 }}>
            <button
              type="button"
              onClick={() =>
                onResult(
                  response.product!,
                  response.source === "off" || response.source === "off_translated"
                    ? "off"
                    : response.source === "usda"
                      ? "usda"
                      : "ai",
                )
              }
              style={{
                height: 34,
                padding: "0 var(--space-3)",
                background: "var(--accent)",
                color: "#0a0b0d",
                border: "1px solid transparent",
                borderRadius: "var(--radius-card)",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "var(--type-body-s)",
              }}
            >
              Use this match
            </button>
            <button
              type="button"
              onClick={onPhotoCta}
              style={{
                height: 34,
                padding: "0 var(--space-3)",
                background: "transparent",
                color: "var(--fg)",
                border: "1px solid var(--hair-2)",
                borderRadius: "var(--radius-card)",
                cursor: "pointer",
                fontSize: "var(--type-body-s)",
              }}
            >
              Snap a label instead
            </button>
          </div>
        </div>
      ) : (
        <div
          style={{
            padding: "var(--space-3)",
            background: "var(--bg-3)",
            border: "1px solid var(--hair-2)",
            borderRadius: "var(--radius-card)",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div style={{ fontSize: "var(--type-body-s)", color: "var(--fg-2)" }}>
            {response.hint ?? "No good match. Take a photo of the back-of-package nutrition table."}
          </div>
          <div>
            <button
              type="button"
              onClick={onPhotoCta}
              style={{
                height: 34,
                padding: "0 var(--space-3)",
                background: "var(--accent)",
                color: "#0a0b0d",
                border: "1px solid transparent",
                borderRadius: "var(--radius-card)",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "var(--type-body-s)",
              }}
            >
              Snap a label
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
